/**
 * Composes Enterprise Executive KPI payload from operational dashboard data.
 * Milestone A — RCM metrics live; finance/clinical tiles show integration CTAs.
 */

import { rcmService } from './rcmService';
import { getEnabledCatalog } from '../config/executiveTileCatalog';
import { customExecutiveMetricsService } from './customExecutiveMetricsService';
import { formatReportingPeriodLabel, normalizeReportingPeriod } from '../utils/reportingPeriod';
import { getServiceApiUrl } from '../utils/constants';
import axios from 'axios';

/** National reference benchmarks (seed — replace with benchmark service) */
const NATIONAL_BENCHMARKS = {
  net_collection_rate: { average: 96.0, unit: 'percent' },
  denial_rate: { average: 7.5, unit: 'percent' },
  days_in_ar: { average: 42.0, unit: 'days' },
  clean_claim_rate: { average: 94.0, unit: 'percent' },
  pos_collection_rate: { average: 55.0, unit: 'percent' },
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthLabel(date) {
  return MONTH_LABELS[date.getMonth()];
}

function denialRateFromOperational(operational) {
  const d = operational?.denials || {};
  const nested = d.denial_summary || {};
  const rate = nested.denial_rate ?? nested.denialRate ?? d.denial_rate ?? d.denialRate;
  return rate != null && rate !== '' ? Number(rate) : null;
}

function cleanClaimRateFromOperational(operational) {
  const c = operational?.claims || {};
  const rate = c.clean_claim_rate ?? c.cleanClaimRate;
  return rate != null && rate !== '' ? Number(rate) : null;
}

function buildSparkline(history) {
  if (Array.isArray(history) && history.length >= 2) {
    return history.map((h) => Number(h.value ?? h));
  }
  return [];
}

function deltaPct(current, prior) {
  if (prior == null || prior === 0) return null;
  return Number((((current - prior) / prior) * 100).toFixed(1));
}

async function fetchCaptureConfidence(organizationId, period) {
  const base = getServiceApiUrl('ANALYTICS_FACADE');
  if (!base || !organizationId) return null;
  const range = normalizeReportingPeriod(period);
  try {
    const response = await axios.post(
      `${base}/api/v1/analytics/query`,
      {
        schema_version: '1.0',
        organization_id: organizationId,
        consumer_service: 'agenticrcm',
        dataset: 'gold_capture_confidence',
        time_range: { start: range.startDate, end: range.endDate },
      },
      { timeout: 8000 }
    );
    const row = response.data?.data?.[0];
    return row || null;
  } catch (err) {
    console.warn('[Executive] gold_capture_confidence unavailable', err?.message || err);
    return null;
  }
}

function captureCaption(row, denomKey) {
  if (!row) return 'Denom printed when Gold books land';
  const n = row[denomKey];
  const sources = (row.ch_sources || []).join(', ') || 'no CH books';
  return `${denomKey}=${n} · ${sources}`;
}

function buildTopKpi(id, label, config, operational) {
  if (!config.live) {
    return {
      id,
      label,
      live: false,
      integrationLabel: config.integrationLabel || 'Connect data source',
      unit: config.unit,
    };
  }

  const value = config.getValue(operational);
  const prior = null;
  const delta = null;
  const numeric = value == null || value === '' ? null : Number(value);

  return {
    id,
    label,
    live: true,
    value: numeric != null && !Number.isNaN(numeric) ? numeric : null,
    unit: config.unit,
    priorLabel: 'vs prior month',
    delta,
    polarity: config.polarity,
    sparkline: buildSparkline(config.history),
    dataStatus: numeric != null && !Number.isNaN(numeric) ? 'ok' : 'empty',
    caption: config.caption || null,
  };
}

function buildOpportunities(operational) {
  const opportunities = [];
  const denialRate = denialRateFromOperational(operational);
  const underpayments = operational.arPosting?.underpayments_open ?? operational.arPosting?.open_underpayments;
  const daysInAr = operational.kpis?.daysInAR?.days_in_ar;
  const cleanRate = cleanClaimRateFromOperational(operational);

  if (denialRate != null && denialRate > 7) {
    opportunities.push({
      id: 'reduce_denials',
      title: 'Reduce denials',
      description: 'Denial rate exceeds industry benchmark — review top CARC patterns and appeal queue.',
      estimatedImpact: Math.round(denialRate * 120000),
      link: '/denials',
      priority: 1,
      impact_estimated: true,
    });
  }

  if (daysInAr != null && daysInAr > 40) {
    opportunities.push({
      id: 'reduce_ar_days',
      title: 'Accelerate A/R collections',
      description: 'Days in A/R above target — prioritize aging follow-up and patient collections.',
      estimatedImpact: Math.round((daysInAr - 35) * 25000),
      link: '/ar-posting',
      priority: 2,
      impact_estimated: true,
    });
  }

  if (underpayments > 0) {
    opportunities.push({
      id: 'underpayments',
      title: 'Resolve underpayments',
      description: `${underpayments} open underpayment(s) detected from remittance analysis.`,
      estimatedImpact: Math.round(underpayments * 850),
      link: '/eob',
      priority: 3,
      impact_estimated: true,
    });
  }

  if (cleanRate != null && cleanRate < 94) {
    opportunities.push({
      id: 'clean_claims',
      title: 'Improve clean claim rate',
      description: `Clean claim rate is ${cleanRate}% — scrub claims before submission to reduce rework.`,
      estimatedImpact: null,
      link: '/claims',
      priority: 4,
    });
  }

  return opportunities.sort((a, b) => a.priority - b.priority).slice(0, 5);
}

function buildAlerts(operational) {
  const alerts = [];
  const denialRate = denialRateFromOperational(operational) ?? 0;
  const daysInAr = operational.kpis?.daysInAR?.days_in_ar ?? 0;

  if (denialRate >= 8) {
    alerts.push({
      id: 'alert-denial-high',
      severity: 'high',
      message: 'Denial rate above 8% — review behavioral health and commercial payer mix.',
    });
  }
  if (daysInAr >= 40) {
    alerts.push({
      id: 'alert-ar-days',
      severity: 'medium',
      message: `Days in A/R at ${daysInAr.toFixed(1)} — above 40-day target.`,
    });
  }
  if ((operational.claimsWorkQueue?.pending_count || 0) > 20) {
    alerts.push({
      id: 'alert-claims-queue',
      severity: 'medium',
      message: `${operational.claimsWorkQueue.pending_count} claims pending in work queue.`,
    });
  }
  const payerAnomalies = operational.denials?.payer_anomalies || [];
  if (payerAnomalies.length) {
    const names = payerAnomalies
      .slice(0, 3)
      .map((row) => row.payer_name)
      .filter(Boolean)
      .join(', ');
    alerts.push({
      id: 'alert-payer-anomaly',
      severity: 'medium',
      message: `${payerAnomalies.length} payer ANOMALY period(s) on denial-by-payer analytics${names ? `: ${names}` : ''}.`,
    });
  }
  if (!alerts.length) {
    alerts.push({
      id: 'alert-ok',
      severity: 'low',
      message: 'No critical RCM alerts for this period.',
    });
  }
  return alerts;
}

function buildRevenueMix(operational) {
  const payerMix = operational.denials?.by_payer || operational.claims?.by_payer;
  const anomalies = new Set(
    (operational.denials?.payer_anomalies || [])
      .map((row) => String(row.payer_name || row.name || '').toLowerCase())
      .filter(Boolean),
  );
  if (Array.isArray(payerMix) && payerMix.length) {
    return payerMix.slice(0, 5).map((p, i) => {
      const name = p.payer_name || p.name || `Payer ${i + 1}`;
      const flagged = p.anomaly || anomalies.has(String(name).toLowerCase());
      return {
        name: flagged ? `${name} · anomaly` : name,
        value: p.amount || p.total || p.count || 1,
      };
    });
  }
  // No fabricated payer mix when live mix is absent.
  return [];
}

function buildTrendSeries(operational) {
  const months = [];
  const collections = [];
  const now = new Date();
  const baseCollection = operational.kpis?.netCollectionRate?.net_collection_rate;
  if (baseCollection == null || Number.isNaN(Number(baseCollection))) {
    return { months: [], collections: [], netRevenue: [], data_status: 'unavailable' };
  }
  const base = Number(baseCollection);
  // Flat historical placeholder until period snapshots exist (deterministic, not random).
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(formatMonthLabel(d));
    collections.push(Number(base.toFixed(1)));
  }
  return {
    months,
    collections,
    netRevenue: collections.map((c) => Math.round(c)),
    data_status: 'single_point_repeated',
    note: 'Historical trend series unavailable; showing current NCR across months until snapshots exist.',
  };
}

function buildScorecards(operational) {
  const kpis = operational.kpis || {};
  const denials = operational.denials || {};
  const claims = operational.claims || {};
  const denialRate = denialRateFromOperational(operational);
  const cleanRate = cleanClaimRateFromOperational(operational);

  const revenueCycleMetrics = [
    {
      label: 'Clean claim rate',
      value: cleanRate != null ? `${cleanRate}%` : '—',
      live: cleanRate != null,
    },
    {
      label: 'Days in A/R',
      value: kpis.daysInAR?.days_in_ar != null ? `${Number(kpis.daysInAR.days_in_ar).toFixed(1)}d` : '—',
      live: kpis.daysInAR?.days_in_ar != null,
    },
    {
      label: 'Patient collections',
      value: kpis.posCollections?.pos_collections != null
        ? `$${Math.round(kpis.posCollections.pos_collections).toLocaleString()}`
        : '—',
      live: (kpis.posCollections?.pos_collections || 0) > 0,
    },
    {
      label: 'Denial rate',
      value: denialRate != null ? `${denialRate}%` : '—',
      live: denialRate != null,
    },
  ];

  const cfoMetrics = [
    { label: 'A/R balance', value: '—', live: false, integration: true },
    {
      label: 'Collection rate',
      value: kpis.netCollectionRate?.net_collection_rate != null
        ? `${Number(kpis.netCollectionRate.net_collection_rate).toFixed(1)}%`
        : '—',
      live: kpis.netCollectionRate?.net_collection_rate != null,
    },
    {
      label: 'Denial rate',
      value: denialRate != null ? `${denialRate}%` : '—',
      live: denialRate != null,
    },
    { label: 'Expense ratio', value: '—', live: false, integration: true },
  ];

  const placeholder = (dept) => ({
    id: dept.id,
    title: dept.title,
    live: false,
    metrics: dept.metrics,
    integrationLabel: 'Connect EHR / PM integration',
  });

  return [
    { id: 'revenue_cycle', title: 'Revenue Cycle (CRO)', live: true, metrics: revenueCycleMetrics },
    { id: 'cfo', title: 'CFO / Finance', live: true, metrics: cfoMetrics },
    placeholder({
      id: 'ceo',
      title: 'CEO / Growth',
      metrics: [
        { label: 'Revenue growth', value: '—' },
        { label: 'Patient growth', value: '—' },
        { label: 'Cost per encounter', value: '—' },
      ],
    }),
    placeholder({
      id: 'coo',
      title: 'COO / Operations',
      metrics: [
        { label: 'No-show rate', value: '—' },
        { label: 'Capacity utilization', value: '—' },
        { label: 'Patient cycle time', value: '—' },
      ],
    }),
    placeholder({
      id: 'cmo',
      title: 'CMO / Medical',
      metrics: [
        { label: 'RVUs per provider', value: '—' },
        { label: 'Coding accuracy', value: '—' },
        { label: 'Documentation time', value: '—' },
      ],
    }),
    placeholder({
      id: 'dental',
      title: 'Dental Services',
      metrics: [
        { label: 'Production / dentist', value: '—' },
        { label: 'Treatment acceptance', value: '—' },
        { label: 'Hygiene recall', value: '—' },
      ],
    }),
    placeholder({
      id: 'hr',
      title: 'HR / Workforce',
      metrics: [
        { label: 'Turnover rate', value: '—' },
        { label: 'Vacancy days', value: '—' },
        { label: 'Labor % of revenue', value: '—' },
      ],
    }),
    placeholder({
      id: 'pharmacy',
      title: 'Pharmacy',
      metrics: [
        { label: 'Rx capture rate', value: '—' },
        { label: '340B savings', value: '—' },
        { label: 'Refill adherence', value: '—' },
      ],
    }),
    placeholder({
      id: 'behavioral_health',
      title: 'Behavioral Health',
      metrics: [
        { label: 'Encounters / clinician', value: '—' },
        { label: 'Auth approval rate', value: '—' },
        { label: 'Caseload', value: '—' },
      ],
    }),
  ];
}

function buildBenchmarks(operational) {
  const kpis = operational.kpis || {};
  const denialRate = denialRateFromOperational(operational);

  return [
    {
      id: 'net_collection_rate',
      label: 'Net Collection Rate',
      org: kpis.netCollectionRate?.net_collection_rate ?? null,
      benchmark: NATIONAL_BENCHMARKS.net_collection_rate.average,
      unit: '%',
      live: kpis.netCollectionRate?.net_collection_rate != null,
    },
    {
      id: 'denial_rate',
      label: 'Denial Rate',
      org: denialRate ?? null,
      benchmark: NATIONAL_BENCHMARKS.denial_rate.average,
      unit: '%',
      live: denialRate != null,
    },
    {
      id: 'days_in_ar',
      label: 'Days in A/R',
      org: kpis.daysInAR?.days_in_ar ?? null,
      benchmark: NATIONAL_BENCHMARKS.days_in_ar.average,
      unit: 'days',
      live: kpis.daysInAR?.days_in_ar != null,
    },
  ];
}

export const executiveDashboardService = {
  async getExecutiveDashboardData({
    period,
    compareTo,
    organizationName,
    organizationId,
    includeCustomMetrics = true,
  } = {}) {
    const layoutConfig = organizationId
      ? await customExecutiveMetricsService.getLayoutOverrides(organizationId)
      : { layout: {}, branding: {} };
    const layoutOverrides = layoutConfig.layout || {};
    const branding = layoutConfig.branding || {};
    const catalog = getEnabledCatalog(layoutOverrides);
    const operational = organizationId
      ? await rcmService.getDashboardData(organizationId, period)
      : await rcmService.getDashboardData(undefined, period);
    const capture = await fetchCaptureConfidence(organizationId, period);
    const kpis = operational.kpis || {};
    const claims = operational.claims || {};

    const denialRate = denialRateFromOperational(operational) ?? 0;
    const ncr = kpis.netCollectionRate?.net_collection_rate ?? 0;
    const daysInAr = kpis.daysInAR?.days_in_ar ?? 0;
    const cleanRate = cleanClaimRateFromOperational(operational) ?? 0;

    const topKpis = catalog.topKpis.map((tileDef) => {
      const builders = {
        net_revenue: () =>
          buildTopKpi('net_revenue', tileDef.label, {
            live: false,
            unit: tileDef.unit,
            integrationLabel: 'Connect GL / finance',
          }, operational),
        operating_margin: () =>
          buildTopKpi('operating_margin', tileDef.label, {
            live: false,
            unit: tileDef.unit,
            integrationLabel: 'Connect GL',
          }, operational),
        encounters: () =>
          buildTopKpi('encounters', tileDef.label, {
            live: false,
            unit: tileDef.unit,
            integrationLabel: 'Connect EHR scheduling',
          }, operational),
        net_collection_rate: () =>
          buildTopKpi('net_collection_rate', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => (ncr > 0 ? ncr : null),
          }, operational),
        days_in_ar: () =>
          buildTopKpi('days_in_ar', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => (daysInAr > 0 ? daysInAr : null),
          }, operational),
        denial_rate: () =>
          buildTopKpi('denial_rate', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => (denialRate > 0 ? denialRate : null),
          }, operational),
        observed_capture: () =>
          buildTopKpi('observed_capture', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => capture?.observed_capture_rate_dollar ?? capture?.observed_capture_rate,
            caption: captureCaption(capture, 'union_keys'),
          }, operational),
        ehr_coverage: () =>
          buildTopKpi('ehr_coverage', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => capture?.ehr_coverage_rate_dollar ?? capture?.ehr_coverage_rate,
            caption: captureCaption(capture, 'ehr_billed_keys'),
          }, operational),
        remit_coverage: () =>
          buildTopKpi('remit_coverage', tileDef.label, {
            live: true,
            unit: tileDef.unit,
            polarity: tileDef.polarity,
            getValue: () => capture?.remit_coverage_rate_dollar ?? capture?.remit_coverage_rate,
            caption: captureCaption(capture, 'gold_remit_keys'),
          }, operational),
        cash_on_hand: () =>
          buildTopKpi('cash_on_hand', tileDef.label, {
            live: false,
            unit: tileDef.unit,
            integrationLabel: 'Connect treasury / GL',
          }, operational),
        patient_retention: () =>
          buildTopKpi('patient_retention', tileDef.label, {
            live: false,
            unit: tileDef.unit,
            integrationLabel: 'Connect patient registry',
          }, operational),
      };
      return (builders[tileDef.id] || (() => buildTopKpi(tileDef.id, tileDef.label, { live: false, unit: tileDef.unit }, operational)))();
    });

    const liveCount = topKpis.filter((k) => k.live && k.dataStatus === 'ok').length;

    const enabledScorecardIds = new Set(catalog.scorecards.map((s) => s.id));

    const customTitle = branding.dashboardTitle?.trim();
    const customSubtitle = branding.dashboardSubtitle?.trim();

    const basePayload = {
      branding: {
        title: customTitle || organizationName || 'Enterprise KPI Dashboard',
        subtitle: customSubtitle || (organizationName ? 'Executive KPI Dashboard' : 'Every department drives our financial success'),
      },
      period: formatReportingPeriodLabel(normalizeReportingPeriod(period)),
      reportingPeriod: normalizeReportingPeriod(period),
      compareTo: compareTo || 'prior_month',
      refreshedAt: new Date().toISOString(),
      dataStatus: liveCount >= 2 ? 'partial' : 'empty',
      topKpis,
      scorecards: buildScorecards(operational).filter((c) => enabledScorecardIds.has(c.id)),
      opportunities: buildOpportunities(operational),
      alerts: buildAlerts(operational),
      revenueMix: buildRevenueMix(operational),
      trends: buildTrendSeries(operational),
      benchmarks: buildBenchmarks(operational),
      daysInArDetail: kpis.daysInAR || null,
      monthlySummary: {
        daysInAr: daysInAr > 0 ? daysInAr : null,
        netCollectionRate: ncr > 0 ? ncr : null,
        denialRate: denialRate > 0 ? denialRate : null,
        claimsCreated: claims.total_claims ?? claims.totalClaims ?? null,
        firstSubmissions: claims.submitted_claims ?? null,
      },
      source: operational,
      tileCatalog: catalog,
    };

    return includeCustomMetrics
      ? customExecutiveMetricsService.applyCustomMetrics(basePayload, organizationId)
      : basePayload;
  },
};
